import React, { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Stack, Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WarningIcon from '@mui/icons-material/Warning';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import ConnectWabaDialog from '../../components/environment/dialogs/ConnectWabaDialog';
import RefreshWabaTokenDialog from '../../components/environment/dialogs/RefreshWabaTokenDialog';

/**
 * The WhatsApp number this environment sends from.
 *
 * It used to be reachable only inside Settings → Environments, as a row on an environment card:
 * the most important setup step in the product, behind a concept a one-shop merchant does not
 * have and should not have to learn. Environments still own connections, this shows the one
 * belonging to the environment currently selected, which for almost everybody is the only one.
 */
const daysRemaining = (tokenExpiresAt) => {
  if (!tokenExpiresAt) return null;
  const expiresAt = new Date(tokenExpiresAt);
  return Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));
};

const YourNumberSettings = () => {
  const { selectedEnvironment, getWabaConnection } = useEnvironment();
  const { isDemoMode } = useAuth();
  const { can } = usePermissions();
  const [connectOpen, setConnectOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);

  const connection = selectedEnvironment ? getWabaConnection(selectedEnvironment.id) : null;
  const remaining = daysRemaining(connection?.tokenExpiresAt);
  const expired = remaining !== null && remaining <= 0;
  const expiringSoon = remaining !== null && remaining <= 7;

  if (!selectedEnvironment) {
    return <Alert severity="info">Select an environment to see the number it sends from.</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Your WhatsApp number</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The number your flows go out from, and the one your customers reply to.
          </Typography>

          {isDemoMode ? (
            <Alert severity="info">
              Your flows are running on FlowGate&apos;s number so you can try them. Create an
              account to connect your own.
            </Alert>
          ) : connection ? (
            <Stack spacing={1.5} alignItems="flex-start">
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon fontSize="small" color="success" />
                <Typography variant="body1">
                  {connection.wabaName || connection.wabaId}
                </Typography>
              </Stack>
              {connection.phoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  {connection.phoneNumber}
                </Typography>
              )}
              {remaining !== null && (
                <Chip
                  size="small"
                  icon={expiringSoon ? <WarningIcon /> : undefined}
                  color={expired ? 'error' : expiringSoon ? 'warning' : 'default'}
                  label={expired
                    ? 'Access token expired'
                    : remaining === 1 ? '1 day of access left' : `${remaining} days of access left`}
                />
              )}
              <Stack direction="row" spacing={1}>
                {(expired || expiringSoon) && (
                  <Button variant="contained" size="small" onClick={() => setRefreshOpen(true)}>
                    Refresh access
                  </Button>
                )}
                {/* Until 2026-10-11 a connected number could never be changed, from here or anywhere */}
                {can.modifyWaba && can.disconnectWaba && (
                  <Button variant="outlined" size="small" onClick={() => setChangeOpen(true)}>
                    Change number
                  </Button>
                )}
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1.5} alignItems="flex-start">
              <Typography variant="body2" color="text.secondary">
                No number connected yet. Until one is, flows can be built and previewed but not
                sent to anybody.
              </Typography>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={() => setConnectOpen(true)}
                sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }}
              >
                Connect your number
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Box>
        <ConnectWabaDialog
          open={connectOpen}
          onClose={() => setConnectOpen(false)}
          environment={selectedEnvironment}
        />
        <ConnectWabaDialog
          open={changeOpen}
          onClose={() => setChangeOpen(false)}
          environment={selectedEnvironment}
          replace
        />
        <RefreshWabaTokenDialog
          open={refreshOpen}
          onClose={() => setRefreshOpen(false)}
          environment={selectedEnvironment}
          wabaConnection={connection}
        />
      </Box>
    </Stack>
  );
};

export default YourNumberSettings;
